import { useState, useCallback, useMemo } from "react";
import { useTestRuns, useManifests } from "../hooks/useTestRuns";
import TestRunCard from "../components/TestRunCard";
import TestRunViewer from "../components/TestRunViewer";
import { NAV_HEIGHT } from "../components/NavBar";
import { apiFetch } from "../services/api";
import { createLogger } from "../lib/logger";

const log = createLogger("TestRuns");

const STATUS_FILTERS = ["all", "pending", "running", "passed", "failed", "error"] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

export default function TestRuns() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [appFilter, setAppFilter] = useState<string>(""); // "" = all (D13)
  const [tagFilters, setTagFilters] = useState<Set<string>>(new Set()); // OR-match (D14)
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  // cycle 2 : passe `app` filter au backend (server-side), tags géré côté client.
  const { runs, loading, error, refresh } = useTestRuns({ app: appFilter });

  // ── Run manifest modal ───────────────────────────────────────────────
  const {
    manifests,
    loading: manifestsLoading,
    error: manifestsError,
  } = useManifests();
  const [showRunModal, setShowRunModal] = useState(false);
  const [selectedManifest, setSelectedManifest] = useState<string>("");
  const [overrides, setOverrides] = useState<string>("");
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);

  // ── Batch selection modal ────────────────────────────────────────────
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchSelected, setBatchSelected] = useState<Set<string>>(new Set());
  const [batching, setBatching] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);

  // ── Derived: unique apps / tags pour les filtres ─────────────────────
  const uniqueApps = useMemo(() => {
    const set = new Set<string>();
    for (const m of manifests) {
      if (m.app) set.add(m.app);
    }
    return Array.from(set).sort();
  }, [manifests]);

  const uniqueTags = useMemo(() => {
    const set = new Set<string>();
    for (const m of manifests) {
      for (const t of m.tags) set.add(t);
    }
    return Array.from(set).sort();
  }, [manifests]);

  // manifest_name → tags (lookup pour filtre client-side D14)
  const tagsByManifest = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const m of manifests) map.set(m.name, m.tags);
    return map;
  }, [manifests]);

  // ── Filtered runs (status client-side + tags client-side) ────────────
  const filteredRuns = useMemo(() => {
    return runs.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (tagFilters.size > 0) {
        const runTags = tagsByManifest.get(r.manifest_name) ?? [];
        const matches = runTags.some((t) => tagFilters.has(t));
        if (!matches) return false;
      }
      return true;
    });
  }, [runs, statusFilter, tagFilters, tagsByManifest]);

  const toggleTagFilter = useCallback((tag: string) => {
    setTagFilters((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setStatusFilter("all");
    setAppFilter("");
    setTagFilters(new Set());
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────

  const handleLaunch = useCallback(async () => {
    if (!selectedManifest) return;
    setLaunching(true);
    setLaunchError(null);
    try {
      let parsedOverrides: unknown = undefined;
      if (overrides.trim()) {
        try {
          parsedOverrides = JSON.parse(overrides) as unknown;
        } catch {
          setLaunchError("Overrides must be valid JSON");
          return;
        }
      }
      const res = await apiFetch("/test-runs", {
        method: "POST",
        body: JSON.stringify({
          manifest_name: selectedManifest,
          overrides: parsedOverrides,
        }),
      });
      if (!res.ok) {
        const txt = await res.text();
        setLaunchError(`Error ${res.status}: ${txt.slice(0, 200)}`);
        return;
      }
      setShowRunModal(false);
      setSelectedManifest("");
      setOverrides("");
      refresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      log.error("launch failed", { error: msg });
      setLaunchError(msg);
    } finally {
      setLaunching(false);
    }
  }, [selectedManifest, overrides, refresh]);

  const handleBatchRun = useCallback(async () => {
    if (batchSelected.size === 0) return;
    setBatching(true);
    setBatchError(null);
    try {
      const res = await apiFetch("/test-runs/batch", {
        method: "POST",
        body: JSON.stringify({ manifests: Array.from(batchSelected) }),
      });
      if (!res.ok) {
        const txt = await res.text();
        setBatchError(`Error ${res.status}: ${txt.slice(0, 200)}`);
        return;
      }
      setShowBatchModal(false);
      setBatchSelected(new Set());
      refresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      log.error("batch run failed", { error: msg });
      setBatchError(msg);
    } finally {
      setBatching(false);
    }
  }, [batchSelected, refresh]);

  const toggleBatch = useCallback((name: string) => {
    setBatchSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  // ── Render ────────────────────────────────────────────────────────────

  const hasActiveFilters =
    statusFilter !== "all" || appFilter !== "" || tagFilters.size > 0;

  return (
    <div
      style={{
        // cycle 2 : place sous la NavBar globale (App.tsx).
        height: `calc(100vh - ${NAV_HEIGHT}px)`,
        display: "flex",
        flexDirection: "column",
        background: "#0a0a0a",
        color: "#e5e7eb",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "14px 20px",
          borderBottom: "1px solid #1f2937",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Test Runs</h1>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setShowBatchModal(true)}
          style={btnStyle("#374151", "#e5e7eb")}
        >
          Run Batch
        </button>
        <button
          onClick={() => setShowRunModal(true)}
          style={btnStyle("#1d4ed8", "#fff")}
        >
          + Run Manifest
        </button>
      </div>

      {/* Filter chips — status + app + tags (cycle 2: spec 5/6) */}
      <div
        style={{
          padding: "10px 20px",
          borderBottom: "1px solid #111827",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 8,
        }}
      >
        {/* Status chips */}
        <span style={filterLabelStyle}>Status:</span>
        {STATUS_FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setStatusFilter(f)}
            style={{
              background: statusFilter === f ? "#1d4ed8" : "#1f2937",
              color: statusFilter === f ? "#fff" : "#9ca3af",
              border: "none",
              borderRadius: 20,
              padding: "3px 12px",
              fontSize: 12,
              cursor: "pointer",
              fontWeight: statusFilter === f ? 600 : 400,
            }}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}

        {/* App filter (dropdown — server-side via ?app=) */}
        <span style={{ ...filterLabelStyle, marginLeft: 8 }}>App:</span>
        <select
          value={appFilter}
          onChange={(e) => setAppFilter(e.target.value)}
          disabled={manifestsLoading}
          style={{
            background: appFilter !== "" ? "#1d4ed8" : "#1f2937",
            color: appFilter !== "" ? "#fff" : "#9ca3af",
            border: "none",
            borderRadius: 20,
            padding: "3px 12px",
            fontSize: 12,
            cursor: "pointer",
            fontWeight: appFilter !== "" ? 600 : 400,
          }}
        >
          <option value="">All</option>
          {uniqueApps.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>

        {/* Tags filter (chips multi-select — client-side via manifest meta) */}
        {uniqueTags.length > 0 && (
          <>
            <span style={{ ...filterLabelStyle, marginLeft: 8 }}>Tags:</span>
            {uniqueTags.map((t) => {
              const active = tagFilters.has(t);
              return (
                <button
                  key={t}
                  onClick={() => toggleTagFilter(t)}
                  style={{
                    background: active ? "#1d4ed8" : "#1f2937",
                    color: active ? "#fff" : "#9ca3af",
                    border: "none",
                    borderRadius: 20,
                    padding: "3px 10px",
                    fontSize: 11,
                    cursor: "pointer",
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  {t}
                </button>
              );
            })}
          </>
        )}

        <div style={{ flex: 1 }} />
        {hasActiveFilters && (
          <button onClick={clearFilters} style={btnStyle("#1f2937", "#9ca3af")}>
            Clear
          </button>
        )}
        <button
          onClick={() => refresh()}
          style={btnStyle("#1f2937", "#9ca3af")}
        >
          ↻
        </button>
      </div>

      {/* Body: list + viewer */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Runs list */}
        <div
          style={{
            width: selectedRunId ? "340px" : "100%",
            flexShrink: 0,
            overflowY: "auto",
            padding: "12px 16px",
            borderRight: selectedRunId ? "1px solid #1f2937" : "none",
          }}
        >
          {loading && (
            <p style={{ color: "#6b7280", fontSize: 13 }}>Loading…</p>
          )}
          {error && (
            <p style={{ color: "#fca5a5", fontSize: 13 }}>Error: {error}</p>
          )}
          {!loading && filteredRuns.length === 0 && (
            <p style={{ color: "#6b7280", fontSize: 13 }}>
              No runs
              {hasActiveFilters ? " matching current filters" : ""}.
            </p>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {filteredRuns.map((run) => (
              <TestRunCard
                key={run.id}
                run={run}
                selected={selectedRunId === run.id}
                onClick={() =>
                  setSelectedRunId((prev) =>
                    prev === run.id ? null : run.id,
                  )
                }
                replayUrl={
                  run.session_id
                    ? `/sessions/${run.session_id}/replay`
                    : undefined
                }
              />
            ))}
          </div>
        </div>

        {/* Viewer panel */}
        {selectedRunId && (
          <div style={{ flex: 1, padding: "12px 16px", overflow: "hidden" }}>
            <TestRunViewer
              runId={selectedRunId}
              onClose={() => setSelectedRunId(null)}
            />
          </div>
        )}
      </div>

      {/* Run Manifest modal */}
      {showRunModal && (
        <Modal onClose={() => setShowRunModal(false)} title="Run Manifest">
          <label style={labelStyle}>Manifest</label>
          <select
            value={selectedManifest}
            onChange={(e) => setSelectedManifest(e.target.value)}
            style={selectStyle}
            disabled={manifestsLoading}
          >
            <option value="">
              {manifestsLoading
                ? "Loading…"
                : manifestsError
                  ? "Error loading manifests"
                  : "— select —"}
            </option>
            {manifests.map((m) => (
              <option key={m.name} value={m.name}>
                {m.name}
                {m.app ? ` (${m.app})` : ""}
              </option>
            ))}
          </select>
          {manifestsError && (
            <p style={{ color: "#fca5a5", fontSize: 11, marginTop: 4 }}>
              {manifestsError}
            </p>
          )}
          <label style={{ ...labelStyle, marginTop: 12 }}>
            Overrides (JSON, optional)
          </label>
          <textarea
            value={overrides}
            onChange={(e) => setOverrides(e.target.value)}
            placeholder='{"VAR": "value"}'
            rows={3}
            style={{
              ...selectStyle,
              fontFamily: "monospace",
              resize: "vertical",
            }}
          />
          {launchError && (
            <p style={{ color: "#fca5a5", fontSize: 12, marginTop: 8 }}>
              {launchError}
            </p>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button
              onClick={() => setShowRunModal(false)}
              style={btnStyle("#374151", "#e5e7eb")}
            >
              Cancel
            </button>
            <button
              onClick={() => void handleLaunch()}
              disabled={!selectedManifest || launching}
              style={btnStyle(
                !selectedManifest || launching ? "#1e3a5f" : "#1d4ed8",
                "#fff",
              )}
            >
              {launching ? "Launching…" : "Run"}
            </button>
          </div>
        </Modal>
      )}

      {/* Run Batch modal */}
      {showBatchModal && (
        <Modal
          onClose={() => {
            setShowBatchModal(false);
            setBatchSelected(new Set());
          }}
          title="Run Batch"
        >
          <p style={{ fontSize: 12, color: "#9ca3af", margin: "0 0 10px" }}>
            Select manifests to run in parallel:
          </p>
          <div
            style={{
              maxHeight: 260,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            {manifestsLoading && (
              <p style={{ color: "#6b7280", fontSize: 13 }}>Loading…</p>
            )}
            {manifestsError && (
              <p style={{ color: "#fca5a5", fontSize: 12 }}>
                Error: {manifestsError}
              </p>
            )}
            {manifests.map((m) => (
              <label
                key={m.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 10px",
                  background: batchSelected.has(m.name) ? "#1e3a5f" : "#1f2937",
                  borderRadius: 5,
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                <input
                  type="checkbox"
                  checked={batchSelected.has(m.name)}
                  onChange={() => toggleBatch(m.name)}
                  style={{ accentColor: "#3b82f6" }}
                />
                <span style={{ flex: 1 }}>{m.name}</span>
                {m.app && (
                  <span style={{ color: "#6b7280", fontSize: 11 }}>
                    {m.app}
                  </span>
                )}
              </label>
            ))}
          </div>
          {batchError && (
            <p style={{ color: "#fca5a5", fontSize: 12, marginTop: 8 }}>
              {batchError}
            </p>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button
              onClick={() => {
                setShowBatchModal(false);
                setBatchSelected(new Set());
              }}
              style={btnStyle("#374151", "#e5e7eb")}
            >
              Cancel
            </button>
            <button
              onClick={() => void handleBatchRun()}
              disabled={batchSelected.size === 0 || batching}
              style={btnStyle(
                batchSelected.size === 0 || batching ? "#1e3a5f" : "#1d4ed8",
                "#fff",
              )}
            >
              {batching
                ? "Launching…"
                : `Run ${batchSelected.size > 0 ? batchSelected.size : ""} Manifests`}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "#111827",
          border: "1px solid #1f2937",
          borderRadius: 10,
          padding: "24px",
          minWidth: 360,
          maxWidth: 480,
          width: "90vw",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            {title}
          </h2>
          <div style={{ flex: 1 }} />
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#6b7280",
              fontSize: 20,
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Style helpers ─────────────────────────────────────────────────────────────

function btnStyle(bg: string, color: string): React.CSSProperties {
  return {
    background: bg,
    color,
    border: "none",
    borderRadius: 6,
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  };
}

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  color: "#9ca3af",
  marginBottom: 5,
};

const filterLabelStyle: React.CSSProperties = {
  fontSize: 11,
  color: "#6b7280",
  fontWeight: 500,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

const selectStyle: React.CSSProperties = {
  width: "100%",
  background: "#1f2937",
  color: "#e5e7eb",
  border: "1px solid #374151",
  borderRadius: 6,
  padding: "7px 10px",
  fontSize: 13,
  outline: "none",
};
